// Staff chat MVP (SR-14): live delivery over the Notifications hub (`/api/notifications/NotificationsHub`, the token as
// `?access_token=`, through the shared createHubConnection and `env.apiBaseUrl`, as the Identity hub does). Mounted once
// in the app shell, and it connects ONLY with the `?x=staff-chat` switch on and a signed-in user.
//
// Events fold into the TanStack Query caches (staff-chat-cache.ts). On a reconnect every staff-chat cache is read again,
// because events sent while the socket was down are not replayed.
//
// Never log a payload here: it carries message bodies.
import { useEffect } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/auth/auth-store';
import { createHubConnection } from '@/shared/realtime/signalr-connection';
import { staffChatKeys } from '@/staff-chat/services/staff-chat-keys';
import { useOpenThreadStore } from '@/staff-chat/lib/open-thread-store';
import { useHubStatusStore } from '@/staff-chat/lib/hub-status-store';
import { applyStaffChatEvent, type StaffChatEvent } from './staff-chat-cache';

export const NOTIFICATIONS_HUB_PATH = '/api/notifications/NotificationsHub';
export const STAFF_CHAT_EVENTS: readonly StaffChatEvent['name'][] = ['StaffChatMessage', 'StaffChatRead', 'StaffChatUnread', 'StaffChatThread'];

export function useStaffChatHub(enabled: boolean): void {
  const qc = useQueryClient();
  const status = useAuthStore((s) => s.status);
  const me = useAuthStore((s) => s.user?.userId);
  const orgId = useAuthStore((s) => s.user?.organizationId);

  // Synchronises with an external system: the SignalR socket.
  useEffect(() => {
    if (!enabled || status !== 'authenticated' || !me) return;
    const who = `${orgId ?? ''}|${me}`;
    const hub = createHubConnection(NOTIFICATIONS_HUB_PATH, () => useAuthStore.getState().getValidAccessToken());

    // Every handler checks `disposed`: after a logout or a user change, a late event never writes the old user's data.
    let disposed = false;
    for (const name of STAFF_CHAT_EVENTS) {
      hub.on(name, (payload: unknown) => {
        if (disposed) return;
        // "Seen": another person's read time, for the dock's line under the caller's last message (never the body).
        if (name === 'StaffChatRead') {
          const p = payload as { threadId?: unknown; userId?: unknown; readAt?: unknown } | null;
          if (p && typeof p.threadId === 'string' && typeof p.readAt === 'string' && p.userId !== me)
            useHubStatusStore.getState().notePeerRead(who, p.threadId, p.readAt);
        }
        applyStaffChatEvent(qc, { name, payload } as StaffChatEvent, {
          who,
          me,
          openThreadId: useOpenThreadStore.getState().openThreadId,
        });
      });
    }
    // Events sent while the socket was down are not replayed: read every staff-chat cache again. Messages still being
    // sent, or that failed, survive the re-read (useThreadMessages keeps them: keepUnsentMessages).
    // The dock's reconnecting strip follows the socket.
    const setHubStatus = useHubStatusStore.getState().setStatus;
    hub.onreconnecting(() => {
      if (!disposed) setHubStatus('reconnecting');
    });
    hub.onclose(() => {
      if (!disposed) setHubStatus('idle');
    });
    hub.onreconnected(() => {
      if (disposed) return;
      setHubStatus('connected');
      void qc.invalidateQueries({ queryKey: staffChatKeys(who).all() });
    });

    void (async () => {
      try {
        await hub.start();
        if (!disposed) setHubStatus('connected');
        if (disposed && hub.state !== HubConnectionState.Disconnected) await hub.stop();
      } catch {
        /* withAutomaticReconnect handles transient failures; the lists still read on focus */
      }
    })();

    return () => {
      disposed = true;
      setHubStatus('idle');
      for (const name of STAFF_CHAT_EVENTS) hub.off(name);
      // Connected, Connecting and Reconnecting all hold (or are about to hold) a socket: stop each of them.
      if (hub.state !== HubConnectionState.Disconnected) void hub.stop().catch(() => {});
    };
  }, [enabled, status, me, orgId, qc]);
}
